/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { getStore } from 'store/accessor';
import { visualActions } from 'modules/visual/slice';
import { isTokenExpired } from '../helpers/jwt';
import API from '../auth/constants';
import { authActions } from '../auth/slice';
import TIME_OUTS from '../constants/time-outs';
import refreshClient from './refresh-client';
//
const getRefreshTokenFromStore = () => getStore()?.getState()['feature/base-auth']?.tokens?.refreshToken;
//
const isRefreshTokenValid = (token) => !isTokenExpired(token);
//
const sleep = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});
//
const isSessionTimedOut = () => Boolean(getStore()?.getState()['feature/visual']?.isSessionTimedOut);
//
const endSession = (message) => {
  getStore()?.dispatch(authActions.refreshTokenFailed(message));
  if (!isSessionTimedOut()) {
    getStore()?.dispatch(visualActions.setSessionTimeOut(true));
  }
};
//
export class RefreshTokenError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'RefreshTokenError';
    this.code = 'REFRESH_FAILED';
    this.cause = cause;
  }
}
//
const doRefresh = async () => {
  const refreshToken = getRefreshTokenFromStore();
  if (!isRefreshTokenValid(refreshToken)) {
    endSession('Refresh token expired');
    throw new RefreshTokenError('Refresh token expired');
  }
  //
  const delays = TIME_OUTS.REFRESH_TOKEN_RETRY_DELAYS;
  let lastError;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const { data } = await refreshClient.post(
        API.POST_REFRESH_TOKENS.path,
        { refreshToken },
        {
          timeout: TIME_OUTS.AUTH_REQUEST_TIMEOUT_MS,
        }
      );
      getStore()?.dispatch(authActions.refreshTokenSucceeded(data));
      return data;
    } catch (err) {
      lastError = err;
      if (attempt < delays.length) {
        // eslint-disable-next-line no-await-in-loop
        await sleep(delays[attempt]);
      }
    }
  }
  endSession(lastError?.message);
  throw new RefreshTokenError(lastError?.message, lastError);
};
//
let inFlight = null;
//
export const refreshTokens = () => {
  if (inFlight) {
    return inFlight;
  }
  inFlight = doRefresh().finally(() => {
    inFlight = null;
  });
  return inFlight;
};
//
export default refreshTokens;
